<template>
	<!-- Registry Page Container -->
	<div class="flex flex-col items-center w-full min-h-(--min-height-custom-mobile) lg:min-h-(--min-height-custom) gap-4">
		<span class="flex text-5xl md:text-7xl w-full justify-center text-center pt-8">Registry</span>
		<Divider class="w-32!" />

		<!-- Gift Provider Container -->
		<div class="flex flex-col w-[80dvw] lg:w-[75dvw] rounded-sm p-4">
			<!-- Gift Provider List -->
			<div class="grid grid-cols-[repeat(auto-fit,12rem)] justify-around w-full gap-4">
				<!-- Loading Card -->
				<div
					v-for="_ in 2"
					v-if="!contentLoaded"
					:key="_"
					class="size-48"
				>
					<LoadingCard
						show-loading-text
						show-borders
						class="size-full!"
					/>
				</div>
				<GiftProvider
					v-for="provider in giftProviders"
					v-if="contentLoaded"
					:key="provider.__kptID"
					v-bind="provider"
				/>
			</div>
		</div>

		<!-- Wishlist Container -->
		<div class="flex flex-col w-full lg:w-[90dvw] pb-2 rounded-sm lg:border border-(--primary-brown) lg:bg-(--card-background)/75">
			<!-- Top Row Container -->
			<div class="flex flex-col lg:flex-row w-full py-2 lg:rounded-t-xs items-center justify-between lg:bg-(--primary-brown) lg:text-(--primary-beige)">
				<!-- Wishlist Header -->
				<h1 class="flex text-2xl lg:pl-4">
					Our Wishlist
				</h1>

				<div class="flex lg:hidden px-4 gap-4 w-full lg:border-b border-(--primary-brown) items-center">
					<Button
						class="!group relative w-1/4 md:w-1/2 my-2 bg-(--primary-beige)! hover:bg-(--card-background)/30! text-(--primary-brown)! border-(--primary-brown)! active:border-(--secondary-brown)! duration-(--default-transition-duration)!"
						type="button"
						label="Filters"
						@click="toggle"
					/>
					<Popover
						ref="popover"
						class="w-[calc(80dvw-2rem)] text-(--primary-brown)! border-(--primary-brown)!"
					>
						<div class="flex flex-col w-full gap-4">
							<WishListFilter
								v-for="(option, index) in wishListFilterOptions"
								:key="index"
								v-model:filter-selection="filterSelection"
								:category="option.category"
								:options="option.options"
								@filter-wishlist="filterItems"
							/>
						</div>
					</Popover>
					<!-- Sort By Dropdown -->
					<Select
						v-model="sortSelection"
						v-ripple
						class="w-3/4 md:w-1/2 max-h-10.5"
						:options="sortOptions"
						option-label="displayLabel"
						placeholder="Sort By"
						:show-clear="sortSelection.displayLabel.length > 0"
						@change="sortItems"
					/>
				</div>
			</div>

			<!-- Wishlist Content Container -->
			<div class="flex flex-col items-center lg:items-start lg:flex-row w-full border-t border-(--primary-brown)">
				<!-- Wishlist Filter Container -->
				<div class="hidden lg:flex flex-col flex-1 min-w-1/5 h-[75dvh] gap-4 p-4">
					<!-- Sort By Dropdown -->
					<Select
						v-model="sortSelection"
						v-ripple
						:options="sortOptions"
						option-label="displayLabel"
						placeholder="Sort By"
						:show-clear="sortSelection.displayLabel.length > 0"
						fluid
						@update:model-value="sortItems"
					/>
					<WishListFilter
						v-for="(option, index) in wishListFilterOptions"
						:key="index"
						v-model:filter-selection="filterSelection"
						:category="option.category"
						:options="option.options"
						@filter-wishlist="filterItems"
					/>
				</div>

				<!-- Wish List Item Container -->
				<div class="grid grid-cols-[repeat(auto-fit,240px)] w-full h-[75dvh] gap-4 p-4 border-l border-(--primary-brown) bg-(--primary-beige) justify-center overflow-auto">
					<!-- Loading Card -->
					<div
						v-for="_ in 4"
						v-if="!contentLoaded"
						:key="_"
						class="w-60 h-84"
					>
						<LoadingCard
							show-loading-text
							show-borders
							class="size-full!"
						/>
					</div>

					<WishListItem
						v-for="wishListItem in paginatedWishListItems"
						v-if="contentLoaded"
						:key="wishListItem.__kptID"
						v-bind="wishListItem"
						@item-purchased="(itemID: string) => updateItemAfterPurchase(itemID)"
					/>
				</div>
			</div>

			<!-- Pagination -->
			<Paginator
				v-model:first="firstRecord"
				v-model:rows="numRows"
				:total-records="totalNumberOfRecords"
				:rows-per-page-options="rowsPerPageOptions"
				:page-link-size="pageLinkSize"
				class="w-full border-t border-(--primary-brown)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import GiftProvider from "~/components/registry/GiftProvider.vue";
	import WishListItem from "~/components/registry/WishListItem.vue";
	import WishListFilter from "~/components/registry/WishListFilter.vue";
	import {
		useRegistryUtils
	} from "~/composables/registry/useRegistryUtils";
	import {
		useSupabase
	} from "~/composables/supabase/useSupabase";
	import type {
		IWishListItem
	} from "~/types/supabase";
	import type {
		SortOption,
		IWishListFilter
	} from "~/types/registry";
	import {
		Divider
	} from "primevue";
	import Paginator from "primevue/paginator";
	import {
		useWindowSize
	} from "@vueuse/core";

	const {
		getGiftProviders,
		getWishListItems
	} = useSupabase();

	const {
		sortOptions,
		sortWishListItems,
		filterWishListItems
	} = useRegistryUtils();

	const { width: windowWidth } = useWindowSize();
	const { contentLoaded, giftProviders, wishListItems } = imageDataStores();
	const popover = ref();
	const firstRecord = ref( 0 );
	const numRows = ref( 10 );
	const rowsPerPageOptions = ref( [
		10,
		20,
		30
	] );
	const pageLinkSize = computed( () => {
		if ( windowWidth.value < 640 ) {
			return 2;
		} else if ( windowWidth.value >= 640 && windowWidth.value < 768 ) {
			return 3;
		} else {
			return 5;
		}
	} );
	const totalNumberOfRecords = computed( () => wishListItemsFiltered.value.length );
	const wishListItemsFiltered = ref<IWishListItem[]>( [] );
	const paginatedWishListItems = computed<IWishListItem[]>( () => wishListItemsFiltered.value.slice( firstRecord.value, firstRecord.value + numRows.value ) );
	const filterSelection = ref<IWishListFilter[]>( [] );
	const wishListFilterOptions = useState<IWishListFilter[]>( "wish-list-filter-options", () => [
		{
			category: "Price",
			options: [ "Under $25", "$25 - $50", "$50 - $100", "$100 - $500", "$500 - $1,000", "$1,000+" ]
		},
		{
			category: "Store",
			options: []
		}
	] );
	const sortSelection = ref<SortOption>( {
		displayLabel: "",
		sortValue: "",
		sortDirection: ""
	} );
	wishListFilterOptions.value.forEach( ( option: IWishListFilter ) => {
		filterSelection.value.push( {
			category: option.category,
			options: []
		} );
	} );

	/**
	 * Helper function for retrieving the gift providers from Supabase and updating the local state.
	 * @return {void}
	 */
	const getGiftProviderComponentData = async (): Promise<void> => {
		contentLoaded.value = false;
		giftProviders.value = await getGiftProviders();
		contentLoaded.value = true;
	};

	/**
	 * Helper function for retrieving the wishlist items from Supabase and updating the local state.
	 * @return {void}
	 */
	const getWishlistItemComponentData = async (): Promise<void> => {
		contentLoaded.value = false;
		wishListItems.value = await getWishListItems();
		wishListItemsFiltered.value = wishListItems.value;
		contentLoaded.value = true;
	};

	/**
	 * Helper function for getting the wishlist items filtered by the current filter selection, in their
	 * default (unsorted) order.
	 * @return {IWishListItem[]}
	 */
	const getDefaultOrderedWishListItems = (): IWishListItem[] => filterWishListItems( filterSelection.value, wishListItems.value );

	/**
	 * Helper wrapper function for sorting the filtered wishlist items based on the current sort selection.
	 * Clearing the sort selection restores the default (unsorted) order rather than leaving the list
	 * in whatever order the last active sort left it in.
	 * @return {void}
	 */
	const sortItems = (): void => {
		if ( !sortSelection.value ) {
			// PrimeVue's Select clear button writes `null` into the v-model instead of resetting
			// to an empty SortOption, so restore the shape the rest of this file expects.
			sortSelection.value = {
				displayLabel: "",
				sortValue: "",
				sortDirection: ""
			};
		}

		const defaultOrderedItems = getDefaultOrderedWishListItems();
		wishListItemsFiltered.value = sortSelection.value.sortValue ?
			sortWishListItems( sortSelection.value, defaultOrderedItems ) :
			defaultOrderedItems;
		firstRecord.value = 0;
	};

	/**
	 * Helper wrapper function for filtering / sorting the wishlist items based on the current filter selection.
	 * @return {void}
	 */
	const filterItems = (): void => {
		sortItems();
	};

	/**
	 * Toggles the visibility of the popover filter component on mobile devices.
	 * @param {Event} event
	 * @return {void}
	 */
	const toggle = ( event: Event ): void => popover.value.toggle( event );

	/**
	 * Emit handler function that updates the quantity needed display for a wishlist item after a purchase.
	 * @param {string} itemID
	 * @return {void}
	 */
	const updateItemAfterPurchase = ( itemID: string ): void => {
		const itemIndex = wishListItems.value.findIndex( item => item.__kptID === itemID );
		if ( wishListItems.value[itemIndex] && itemIndex !== -1 ) {
			wishListItems.value[itemIndex].quantityNeeded = Math.max( 0, wishListItems.value[itemIndex].quantityNeeded - 1 );
			filterItems();
		}
	};

	watch( contentLoaded, () => {
		if ( !contentLoaded.value ) return;

		if ( !giftProviders.value || giftProviders.value.length === 0 ) {
			// If the content loaded flag is true but the array is not populated, get the data and populate
			getGiftProviderComponentData();
		}
		if ( !wishListItems.value || wishListItems.value.length === 0 || !wishListItemsFiltered.value || wishListItemsFiltered.value.length === 0 ) {
			// If the content loaded flag is true but the array is not populated, get the data and populate
			getWishlistItemComponentData();
		}
	} );

	watch( giftProviders, () => {
		wishListFilterOptions.value.forEach( ( option: IWishListFilter ): void => {
			if ( option.category === "Store" ) {
				option.options = giftProviders.value.map( provider => provider.name );
				option.options.push( "Other" );
			}
		} );
	} );

	onMounted( () => {
		wishListItemsFiltered.value = wishListItems.value;
		wishListFilterOptions.value.forEach( ( option: IWishListFilter ): void => {
			if ( option.category === "Store" ) {
				option.options = giftProviders.value.map( provider => provider.name );
				option.options.push( "Other" );
			}
		} );
	} );
</script>

<style scoped>

</style>